import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import Swal from "sweetalert2";

const VendorPaymentList = () => {
  const location = useLocation();
  const [paymentData, setPaymentData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get current month's first and last day
  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatDate = (date) => {
      return date.toISOString().split("T")[0];
    };

    return {
      firstDay: formatDate(firstDay),
      lastDay: formatDate(lastDay),
    };
  };

  const { firstDay, lastDay } = getCurrentMonthDates();
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [uniqueVendors, setUniqueVendors] = useState(0);

  const fetchData = async (filterStartDate = "", filterEndDate = "") => {
    setLoading(true);

    try {
      // Build query parameters for date filtering
      let queryParams = "";
      if (filterStartDate && filterEndDate) {
        queryParams = `?start_date=${filterStartDate}&end_date=${filterEndDate}`;
      } else if (filterStartDate) {
        queryParams = `?start_date=${filterStartDate}`;
      } else if (filterEndDate) {
        queryParams = `?end_date=${filterEndDate}`;
      }

      const res = await Http.get(`/accounts/vendor-payments${queryParams}`);
      const resRole = await Http.get(`/staff-type-check`);

      // Helper to detect ISO UTC date strings
      const isIsoUtcDateString = (str) =>
        typeof str === "string" &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(str);

      const formatUtcToLocalDisplay = (isoStr) => {
        const d = new Date(isoStr); // parsed as UTC, represented in local time
        const months = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
        const day = pad2(d.getDate());
        const monthName = months[d.getMonth()];
        const year = d.getFullYear();
        let hours = d.getHours();
        const minutes = pad2(d.getMinutes());
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        if (hours === 0) hours = 12;
        const hh = pad2(hours);
        return `${day}-${monthName}-${year} ${hh}:${minutes} ${ampm}`;
      };

      // Helper to format numeric amount with thousands separators, preserving decimals exactly
      const formatAmountWithGrouping = (val) => {
        if (val === null || val === undefined) return val;
        const str = String(val);
        if (!/^-?\d+(?:\.\d+)?$/.test(str)) return val; // leave as-is if not a pure number
        const isNeg = str.startsWith("-");
        const raw = isNeg ? str.slice(1) : str;
        const [intPart, decPart] = raw.split(".");
        const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const result =
          decPart !== undefined ? `${grouped}.${decPart}` : grouped;
        return isNeg ? `-${result}` : result;
      };

      // Handle the new response structure from backend
      const responseData = res.data.data || res.data; // Handle both old and new response formats
      const totalAmountFromBackend = res.data.total_amount || 0;

      // Transform only top-level date fields and 'amount' on each item; preserve all other fields exactly
      const formattedData = Array.isArray(responseData)
        ? responseData.map((item) => {
            const formatted = { ...item };
            Object.keys(item).forEach((key) => {
              const val = item[key];
              if (key === "date" && val) {
                const d = new Date(val);
                if (!isNaN(d.getTime())) {
                  formatted[key] = d.toISOString().split("T")[0]; // show only date
                } else {
                  formatted[key] = val; // fallback keep original if invalid
                }
                return;
              }
              if (isIsoUtcDateString(val)) {
                formatted[key] = formatUtcToLocalDisplay(val);
              } else if (key === "amount") {
                formatted[key] = formatAmountWithGrouping(val);
              }
            });
            return formatted;
          })
        : responseData;

      setPaymentData(formattedData);
      setTotalAmount(totalAmountFromBackend);
      setTotalRows(formattedData.length);

      // Calculate unique vendors
      const uniqueVendorIds = new Set(
        formattedData.map((item) => item.vendor?.id).filter((id) => id)
      );
      setUniqueVendors(uniqueVendorIds.size);

      setRole(resRole.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    DatatableFunc();
    // Load data with default current month date range
    fetchData(startDate, endDate);
  }, []);

  // Handle date range filter
  const handleDateFilter = () => {
    fetchData(startDate, endDate);
  };

  const clearDateFilter = () => {
    const { firstDay, lastDay } = getCurrentMonthDates();
    setStartDate(firstDay);
    setEndDate(lastDay);
    fetchData(firstDay, lastDay);
  };

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const cols = [
    { field: "payment_number", header: "Payment Number" },
    { field: "vendor.name", header: "Vendor" },
    { field: "bank.name", header: "Bank" },
    { field: "amount", header: "Amount" },
    { field: "date", header: "Date" },
    { field: "description", header: "Description" },
  ];

  const actionTemplate = (item, deleteData) => {
    return (
      <td className="text-xs text-center">
        <Link
          to={`/view-vendor-payment/${item.id}`}
          className="btn btn-info btn-sm ms-1"
          title="View Vendor Payment"
        >
          <i className="fa fa-list" aria-hidden="true"></i>
        </Link>
        {(role === "admin" || role === "accounts") && (
          <Link
            to={`/edit-vendor-payment/${item.id}`}
            className="btn btn-primary btn-sm ml-1"
            title="Edit"
          >
            <i className="fa fa-pencil" aria-hidden="true"></i>
          </Link>
        )}
        {role === "admin" && (
          <button
            className="btn btn-danger btn-sm ml-1"
            onClick={() =>
              deleteData(item.id, "accounts/vendor-payments", "Vendor Payments")
            }
          >
            <i className="fa fa-trash"></i>
          </button>
        )}
      </td>
    );
  };

  const deleteData = (id, deleteApiUrl, showMsg) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        Http.delete("/" + deleteApiUrl + "/" + id).then((res) => {
          // setPaymentData(res.data);
          fetchData();
          Swal.fire("Deleted!", showMsg + " has been deleted.", "success");
        });
      }
    });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">All Vendor Payments</h5>
            <div className="am-right">
              {(role === "admin" || role === "accounts") && (
                <Link to="/create-vendor-payment" className="btn btn-primary">
                  <i className="fa fa-plus"></i> Add New Vendor Payment
                </Link>
              )}
            </div>
          </div>

          <div className="am-pagebody">
            <Alert alert={alert} />

            {/* Date Range Filter Section */}
            <div className="card pd-20 pd-sm-40 mg-b-20">
              {/* Date Range Indicator */}
              <div className="alert alert-info border-0 bg-light-info mb-3">
                <div className="d-flex align-items-center">
                  <i className="fa fa-calendar-alt text-info me-2"></i>
                  <span className="fw-medium">
                    Data showing from:
                    <span className="text-primary mx-2">
                      {new Date(startDate).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                    to
                    <span className="text-primary mx-2">
                      {new Date(endDate).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  </span>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <label className="form-label fw-medium text-dark">
                    <i className="fa fa-calendar-plus text-primary me-1"></i>
                    From Date:
                  </label>
                  <input
                    type="date"
                    className="form-control border-2"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-medium text-dark">
                    <i className="fa fa-calendar-minus text-primary me-1"></i>
                    To Date:
                  </label>
                  <input
                    type="date"
                    className="form-control border-2"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <button
                    className="btn btn-primary me-2 px-4 shadow-sm"
                    onClick={handleDateFilter}
                  >
                    <i className="fa fa-search me-1"></i> Apply Filter
                  </button>
                  <button
                    className="btn btn-outline-secondary px-4 shadow-sm"
                    onClick={clearDateFilter}
                  >
                    <i className="fa fa-refresh me-1"></i> Reset
                  </button>
                </div>
                <div className="col-md-6 offset-md-3 mt-3">
                  {/* Summary Metrics */}
                  <div className="summary-metrics">
                    <div className="metric-card bg-gradient-light p-3 rounded shadow-sm border">
                      <div className="text-center mb-2">
                        <small className="text-muted fw-medium">
                          SUMMARY STATISTICS
                        </small>
                      </div>
                      <div className="row text-center">
                        <div className="col-4">
                          <div className="metric-item">
                            <h6 className="metric-value text-primary mb-1 fw-bold">
                              {totalRows}
                            </h6>
                            <small className="metric-label text-muted">
                              Records
                            </small>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="metric-item">
                            <h6 className="metric-value text-success mb-1 fw-bold">
                              {uniqueVendors}
                            </h6>
                            <small className="metric-label text-muted">
                              Vendors
                            </small>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="metric-item">
                            <h6 className="metric-value text-info mb-1 fw-bold">
                              {totalAmount.toLocaleString()}
                            </h6>
                            <small className="metric-label text-muted">
                              Total
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card pd-20 pd-sm-40">
              <ReactDataTable
                cols={cols}
                receiveData={paymentData}
                isLoading={loading}
                actionTemplate={actionTemplate}
                deleteData={deleteData}
              />

              {/* Footer with Total Amount */}
              <div className="mt-3 pt-3 border-top">
                <div className="row">
                  <div className="col-md-12 text-end">
                    <h5 className="mb-0">
                      <strong>
                        Total Amount: {totalAmount.toLocaleString()}
                      </strong>
                    </h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorPaymentList;
